import { useEffect, useMemo, useState } from "react";
import { Api } from "../../lib/api";
import { LlmDataStore } from "../../lib/cache";
import { Button, StatusDot } from "../../components/ui";
import "./providers.css";

const DEFAULT_LLMS = ["openai", "gemini", "groq", "anthropic", "mistral"];

export default function Providers({ onUrlCopied }) {
  const [pageLlms, setPageLlms] = useState(DEFAULT_LLMS);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedLlm, setExpandedLlm] = useState(null);
  const [credentials, setCredentials] = useState(LlmDataStore.getCredentials().length ? groupByLlm(LlmDataStore.getCredentials()) : {});
  const [catalog, setCatalog] = useState(LlmDataStore.getCatalog());
  const [sessionFullUrls, setSessionFullUrls] = useState({});

  async function refreshCredentials() {
    const list = await Api.listCredentials();
    setCredentials(groupByLlm(list));
    LlmDataStore.saveCredentials(list);
  }

  async function loadCatalog() {
    try {
      const resp = await Api.listAvailableLlms();
      setCatalog(resp.llms || []);
      LlmDataStore.saveCatalog(resp.llms || []);
    } catch {
      // keep cached catalog on failure
    }
  }

  useEffect(() => {
    refreshCredentials();
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generatePassThrough(llm) {
    const resp = await Api.generatePassThrough(llm);
    const list = await Api.listCredentials();
    setCredentials(groupByLlm(list));
    const newCred = list.find((c) => c.llm === llm);
    if (newCred) {
      setSessionFullUrls((prev) => ({ ...prev, [newCred.id]: resp.base_url }));
    }
  }

  async function revoke(id) {
    setSessionFullUrls((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    await Api.deleteCredential(id);
    await refreshCredentials();
  }

  function markCopied(id) {
    setSessionFullUrls((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    onUrlCopied?.(id);
  }

  if (!expandedLlm) {
    return (
      <div>
        <h1 style={{ fontSize: 24 }}>Connected Providers</h1>
        <p style={{ color: "var(--slate)", fontSize: 13.5, marginTop: 4, marginBottom: 20 }}>
          Generate a pass-through URL for each provider you want to monitor.
        </p>

        <ProviderSearchBar
          query={searchQuery}
          onQueryChange={setSearchQuery}
          catalog={catalog}
          alreadyAdded={pageLlms}
          onLlmSelected={(id) => {
            setPageLlms((prev) => (prev.includes(id) ? prev : [...prev, id]));
            setSearchQuery("");
          }}
          onRefreshCatalog={loadCatalog}
        />

        <div style={{ height: 16 }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pageLlms.map((llm) => (
            <ProviderCollapsedRow
              key={llm}
              llm={llm}
              credentials={credentials[llm] || []}
              onExpand={() => setExpandedLlm(llm)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <button className="tg-icon-btn" onClick={() => setExpandedLlm(null)} aria-label="Back">
          <BackIcon />
        </button>
        <h1 style={{ fontSize: 22, textTransform: "capitalize" }}>{expandedLlm}</h1>
      </div>

      <ProviderPassThroughCard
        llm={expandedLlm}
        credentials={credentials[expandedLlm] || []}
        sessionFullUrls={sessionFullUrls}
        onRefresh={refreshCredentials}
        onGenerate={() => generatePassThrough(expandedLlm)}
        onRevoke={revoke}
        onCopied={markCopied}
      />
    </div>
  );
}

function groupByLlm(list) {
  const map = {};
  list.forEach((c) => (map[c.llm] ||= []).push(c));
  return map;
}

function ProviderSearchBar({ query, onQueryChange, catalog, alreadyAdded, onLlmSelected, onRefreshCatalog }) {
  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    return catalog.filter(
      (entry) =>
        !alreadyAdded.includes(entry.id) &&
        (entry.id.toLowerCase().includes(query.toLowerCase()) ||
          entry.display_name.toLowerCase().includes(query.toLowerCase()))
    );
  }, [query, catalog, alreadyAdded]);

  return (
    <div style={{ position: "relative" }}>
      <label className="tg-field">
        <span className="tg-field__label">Search providers</span>
        <span className="tg-field__control">
          <input
            className="tg-field__input"
            placeholder="e.g. deepseek, kimi-k3…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
          {catalog.length === 0 && (
            <button className="tg-field__suffix-btn" onClick={onRefreshCatalog} style={{ pointerEvents: "auto" }}>
              <RefreshIcon />
            </button>
          )}
        </span>
      </label>

      {suggestions.length > 0 && (
        <div className="tg-suggest-dropdown">
          {suggestions.map((entry) => (
            <button key={entry.id} className="tg-suggest-dropdown__item" onClick={() => onLlmSelected(entry.id)}>
              {entry.display_name}
            </button>
          ))}
        </div>
      )}
      {suggestions.length === 0 && query.trim() && (
        <p style={{ fontSize: 12, color: "var(--muted-slate)", marginTop: 6 }}>No matching providers found.</p>
      )}
    </div>
  );
}

function ProviderCollapsedRow({ llm, credentials, onExpand }) {
  const status = aggregateStatus(credentials);
  return (
    <button className="tg-provider-collapsed" onClick={onExpand}>
      <span style={{ flex: 1, textAlign: "left", fontSize: 14, fontWeight: 500, textTransform: "capitalize" }}>
        {llm}
      </span>
      <StatusDot status={status} />
      <span style={{ marginLeft: 8, color: "var(--muted-slate)", fontSize: 16 }}>›</span>
    </button>
  );
}

function aggregateStatus(credentials) {
  if (!credentials.length) return null;
  if (credentials.some((c) => c.status === "green")) return "green";
  if (credentials.some((c) => c.status === "red")) return "red";
  return credentials[0].status;
}

function ProviderPassThroughCard({ llm, credentials, sessionFullUrls, onRefresh, onGenerate, onRevoke, onCopied }) {
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }

  async function copy(text, id) {
    // Plain Clipboard API -- @tauri-apps/plugin-clipboard-manager isn't
    // installed here (this is a regular browser page, not a Tauri
    // webview), and even a dynamic import() of a package that's never
    // installed at all would fail to resolve at Vite build time.
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      // Clipboard API blocked (e.g. insecure context, permissions) --
      // nothing more we can do here.
    }
    onCopied(id);
  }

  return (
    <div className="tg-card">
      <div style={{ display: "flex", alignItems: "center" }}>
        <h3 style={{ fontSize: 18, textTransform: "capitalize", flex: 1 }}>{llm}</h3>
        <button className={`tg-icon-btn ${refreshing ? "tg-spin-icon" : ""}`} onClick={handleRefresh} aria-label="Refresh">
          <RefreshIcon />
        </button>
      </div>

      <div style={{ height: 16 }} />

      <Button variant="outlined" fullWidth onClick={onGenerate} style={{ background: "rgba(46,125,107,0.08)", color: "var(--accent)", border: "none" }}>
        <LinkIcon /> Generate pass-through URL
      </Button>

      {credentials.map((cred) => {
        const statusLabel =
          cred.status === "green" ? "Active" : cred.status === "red" ? "Invalid" : cred.status === "unknown" ? "Waiting…" : cred.status;

        const fullUrl = sessionFullUrls[cred.id];
        const isNew = !!fullUrl;
        const rawUrl = fullUrl || cred.masked_key || `xxx${cred.id.slice(-6)}`;
        const maskedDisplay = isNew || rawUrl.length <= 6 || rawUrl.startsWith("xxx") ? rawUrl : `xxx${rawUrl.slice(-6)}`;

        return (
          <div key={cred.id} className="tg-cred-card">
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StatusDot status={cred.status} />
              <span style={{ fontSize: 11.5, color: "var(--slate)" }}>{statusLabel}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              <span className="tg-cred-card__url">{maskedDisplay}</span>
              <button className="tg-btn tg-btn--text tg-btn--primary" onClick={() => copy(rawUrl, cred.id)}>
                Copy
              </button>
              <button className="tg-icon-btn" onClick={() => onRevoke(cred.id)} aria-label="Revoke" style={{ color: "var(--error)" }}>
                <CloseIcon />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function LinkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
      <path d="M10 13a5 5 0 007.07 0l2.83-2.83a5 5 0 00-7.07-7.07L11.5 4.5" strokeLinecap="round" />
      <path d="M14 11a5 5 0 00-7.07 0L4.1 13.83a5 5 0 007.07 7.07l1.34-1.34" strokeLinecap="round" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
    </svg>
  );
}
